/**
 * Language Database Operations
 *
 * Handles saving and loading language preferences from database
 */

import { invokeEdgeFunctionSafely } from '../supabase-functions-wrapper';
import { systemLogger } from '../logging';
import { type SupportedLanguage } from './constants';

/**
 * Save user language preference to database
 */
export async function saveUserLanguageToDatabase(language: SupportedLanguage): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { data: _data, error } = await invokeEdgeFunctionSafely(
      'update-user-language',
      { body: { language } }
    );

    if (error) {
      systemLogger.error('Language update failed', error);
      return { success: false, error: (error as Error).message || 'Failed to update language' };
    }

    return { success: true };
  } catch (error) {
    systemLogger.error('Language service operation failed', error);
    return { success: false, error: 'Failed to save language preference' };
  }
}

/**
 * Load user language preference after login
 */
export async function loadUserLanguageAfterLogin(_accessToken: string): Promise<{
  language?: SupportedLanguage;
  companyDefaultLanguage?: SupportedLanguage;
  error?: string;
}> {
  try {
    const { data, error } = await invokeEdgeFunctionSafely(
      'get-user-profile-with-language'
    );

    if (error) {
      systemLogger.error('Failed to load user profile with language', error);
      return { error: (error as Error).message || 'Failed to load language preferences' };
    }

    const result: {
      language?: SupportedLanguage;
      companyDefaultLanguage?: SupportedLanguage;
    } = {};

    const typedData = data as unknown as {
      profile?: {
        language?: SupportedLanguage;
        company?: {
          default_language?: SupportedLanguage;
        } | null;
      };
    };

    if (typedData?.profile?.language !== undefined) {
      result.language = typedData.profile.language;
    }

    if (typedData?.profile?.company?.default_language !== undefined) {
      result.companyDefaultLanguage = typedData.profile.company.default_language;
    }

    return result;
  } catch (error) {
    systemLogger.error('Language service operation failed', error);
    return { error: 'Failed to load language preferences' };
  }
}